const positiveInteger = (value) => /^\d+$/.test(String(value)) && Number(value) > 0

const identity = (account = {}) => ({
  environment: account.environment || '',
  nmCcdOrganizationId: Number(account.nmCcdOrganizationId),
  nmCcdLocationId: Number(account.nmCcdLocationId),
})

export const NM_CCD_ENVIRONMENTS = ['Development', 'UAT', 'Production']

export const isAssignedShopInNewMexico = (shop) => {
  if (!shop) return false
  const state = (shop.address && shop.address.state) || shop.stateCode || shop.state || ''
  return String(state).toUpperCase() === 'NM'
}

export const shouldShowNmCcdTab = (shop, flagEnabled) =>
  Boolean(flagEnabled) && isAssignedShopInNewMexico(shop)

export const identitiesMatch = (left, right) => {
  if (!left || !right) return false
  const leftIdentity = identity(left)
  const rightIdentity = identity(right)
  return (
    leftIdentity.environment === rightIdentity.environment &&
    leftIdentity.nmCcdOrganizationId === rightIdentity.nmCcdOrganizationId &&
    leftIdentity.nmCcdLocationId === rightIdentity.nmCcdLocationId
  )
}

export const normalizeNmCcdAccounts = (response) => {
  const accounts = Array.isArray(response) ? response : response ? [response] : []
  return accounts
    .filter(Boolean)
    .map(({ nmCcdJwtToken, nmCcdJwtCiphertext, ...sanitized }) => sanitized)
}

export const normalizeNmCcdAccount = (response, assignedShopId) => {
  const selected = normalizeNmCcdAccounts(response).find(
    (account) => account.shopId === assignedShopId,
  )
  if (!selected) return {}
  return selected
}

export const serializeNmCcdRequest = (account = {}, jwtTokenInput = '', operation = 'preserve') => {
  const payload = {
    ...(account.id ? { id: account.id } : {}),
    ...(account.companyId ? { companyId: account.companyId } : {}),
    shopId: account.shopId,
    complianceType: 'NM_CCD',
    stateCode: 'NM',
    environment: account.environment,
    enabled: Boolean(account.enabled),
    enableSync: Boolean(account.enabled && account.enableSync),
    nmCcdOrganizationId: Number(account.nmCcdOrganizationId),
    nmCcdLocationId: Number(account.nmCcdLocationId),
  }

  if (operation === 'replace') payload.nmCcdJwtToken = jwtTokenInput.trim()
  return payload
}

export const validateNmCcdForm = (
  form,
  jwtTokenInput,
  persistedIdentity,
  { requireIdentity = true } = {},
) => {
  const errors = {}
  if (!form.environment) errors.environment = 'Environment is required.'
  if (requireIdentity && !positiveInteger(form.nmCcdOrganizationId)) {
    errors.nmCcdOrganizationId = 'Organization ID must be a positive integer.'
  }
  if (requireIdentity && !positiveInteger(form.nmCcdLocationId)) {
    errors.nmCcdLocationId = 'Location ID must be a positive integer.'
  }

  const hasReplacement = Boolean((jwtTokenInput || '').trim())
  const savedTokenCompatible =
    Boolean(form.nmCcdJwtTokenConfigured) && identitiesMatch(form, persistedIdentity)
  if (!hasReplacement && !savedTokenCompatible) {
    const identityChanged = Boolean(persistedIdentity) && !identitiesMatch(form, persistedIdentity)
    errors.jwtToken =
      form.enabled && !identityChanged
        ? 'Configure a token before enabling NM CCD.'
        : 'Enter a JWT token for the selected NM CCD identity.'
  }
  return errors
}

export const getNmCcdProviderErrorMessage = (error = {}) => {
  const data = (error.response && error.response.data) || error.data || error
  if (typeof data.errorMessage === 'string' && data.errorMessage.trim()) {
    return data.errorMessage.trim()
  }
  if (typeof data.message === 'string' && data.message.trim()) return data.message.trim()
  const code = String(data.code || data.errorCode || '').toUpperCase()
  if (code.includes('AUTH')) {
    return 'NM CCD rejected the token. Enter a valid location token and try again.'
  }
  if (code.includes('SCOPE') || code.includes('ACCESS') || code.includes('LOCATION')) {
    return 'The token does not have access to retail inventory for its NM CCD location.'
  }
  if (code.includes('TIMEOUT') || error.code === 'ECONNABORTED') {
    return 'NM CCD could not be reached. Your configuration was not changed.'
  }
  return 'NM CCD could not verify this configuration. Try again or contact support.'
}

// Blank input keeps the saved token; the request must then omit nmCcdJwtToken entirely, because
// an empty string wipes the saved token server-side.
export const jwtTokenOperation = (jwtTokenInput = '') =>
  String(jwtTokenInput || '').trim() ? 'replace' : 'preserve'



// WEBPACK FOOTER //
// ./src/utils/nmCcdCompliance.js